import { HttpClient, HttpHeaders } from '@angular/common/http';
import { DestroyRef, Injectable, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { AbstractControl } from '@angular/forms';
import { ModalService } from 'mbs-ui-kit';
import { AccessReviewRequiredModalComponent } from 'mbs-ui-shared@components/access-review-required-modal/access-review-required-modal.component';
import {
  PrePostActionsVerificationAnswer,
  PrePostActionsVerificationModalComponent
} from 'mbs-ui-shared@components/pre-post-actions-verification-modal/pre-post-actions-verification-modal.component';
import { AuthFacade } from 'mbs-ui-shared@facades/auth.facade';
import { MANUAL_ERROR_HANDLED } from 'mbs-ui-shared@interceptors/error-handler.interceptor';
import { Observable, from, of } from 'rxjs';
import { catchError, filter, map } from 'rxjs/operators';

@Injectable({ providedIn: 'root' })
export class ProviderVerificationService {
  private http = inject(HttpClient);
  private auth = inject(AuthFacade);
  private modalService = inject(ModalService);

  get isVerified(): boolean {
    return !!this.auth.currentUserValue?.ProviderInfo?.IsVerified;
  }

  sendVerificationRequest(): Observable<unknown> {
    const admin = this.auth.currentUserValue;

    return this.http.post(
      '/api/provider-verification/send',
      {
        respondent: `${admin?.FirstName ?? ''} ${admin?.LastName ?? ''}`.trim(),
        respondentEmail: admin?.Email
      },
      { headers: new HttpHeaders({ MANUAL_ERROR_HANDLED }) }
    );
  }

  ensureVerified(): Observable<boolean> {
    return this.isVerified ? of(true) : this.openAccessReviewDialog();
  }

  needsPrePostActionsVerification(enabled: boolean): boolean {
    return !!enabled && !this.isVerified;
  }

  // Resolves with the chosen option, or null when the dialog was cancelled or dismissed
  askAboutPrePostActions(): Observable<PrePostActionsVerificationAnswer | null> {
    const settings = { data: { request: this.sendVerificationRequest(), email: this.auth.currentUserValue?.Email } };

    return from(this.modalService.openCustom<PrePostActionsVerificationAnswer>(PrePostActionsVerificationModalComponent, settings)).pipe(
      catchError(() => of(null))
    );
  }

  private openAccessReviewDialog(): Observable<boolean> {
    const settings = { data: { request: this.sendVerificationRequest(), email: this.auth.currentUserValue?.Email } };

    return from(this.modalService.openCustom(AccessReviewRequiredModalComponent, settings)).pipe(
      map(() => false),
      catchError(() => of(false))
    );
  }

  guardDangerousToggle(control: AbstractControl<boolean>, destroyRef: DestroyRef, onRevert?: () => void): void {
    if (!control) return;

    control.valueChanges
      .pipe(
        // `dirty` keeps programmatic patches (loading saved options) from counting as a user action.
        filter((enabled) => enabled && control.dirty && !this.isVerified),
        takeUntilDestroyed(destroyRef)
      )
      .subscribe(() => {
        control.setValue(false, { emitEvent: false });
        onRevert?.();
        this.openAccessReviewDialog().subscribe();
      });
  }
}
